import React from "react";
import PropTypes from "prop-types";
import { flexRender } from "@tanstack/react-table";
import Select from "react-select";

const TableBody = ({
  table,
  hideColumnsData,
  columnSizes,
  rowSizes,
  resizingColumn,
  resizingRow,
  frozenColumns,
  canUpdate,
  localValue,
  dependentValues,
  sourceTableColumnName,
  dropdownOptions,
  handleColumnResizeStart,
  handleRowResizeStart,
  toggleColumnFreeze,
  updateCheckboxValue,
  handleTextInputChange,
  updateRow,
  setStickyNotePopup,
  getFrozenColumnStyle,
  getFrozenColumnLeftPosition,
}) => {
  // Helper function to get dropdown value for Select component
  const getDropdownValue = (cellValue) => {
    if (cellValue !== undefined) {
      return {
        value: cellValue,
        label: cellValue,
      };
    }
    return null;
  };

  // Helper function to get dropdown options based on field name
  const getDropdownOptions = (fieldName, rowOriginal) => {
    if (fieldName === "STN") {
      return (
        dropdownOptions?.map((opt) => ({
          value: opt,
          label: opt,
        })) || []
      );
    }
    if (fieldName === "SFN") {
      return (
        dependentValues[rowOriginal[sourceTableColumnName]]?.map((opt) => ({
          value: opt.columnName,
          label: opt.columnName,
        })) || []
      );
    }
    return [];
  };

  // Helper function to create Select styles
  const getSelectStyles = () => ({
    menuPortal: (base) => ({ ...base, zIndex: 9999 }),
    menu: (provided) => ({
      ...provided,
      minWidth: "250px",
      width: "max-content",
    }),
    control: (provided) => ({
      ...provided,
      width: "100%",
      minHeight: "24px",
      height: "24px",
      fontSize: "12px",
    }),
    valueContainer: (provided) => ({
      ...provided,
      height: "24px",
      padding: "0 4px",
    }),
    input: (provided) => ({
      ...provided,
      margin: "0px",
      padding: "0px",
    }),
    singleValue: (provided) => ({
      ...provided,
      fontSize: "12px",
    }),
    indicatorsContainer: (provided) => ({
      ...provided,
      height: "24px",
    }),
    dropdownIndicator: (provided) => ({
      ...provided,
      padding: "2px",
    }),
    clearIndicator: (provided) => ({
      ...provided,
      padding: "2px",
    }),
    option: (provided) => ({
      ...provided,
      fontSize: "12px",
      padding: "4px 8px",
      whiteSpace: "nowrap",
    }),
  });

  // Helper function to render checkbox cell
  const renderCheckboxCell = (cell, row) => (
    <input
      type="checkbox"
      disabled={!canUpdate}
      checked={cell.getValue() === "Y"}
      onChange={(e) => updateCheckboxValue(row, cell.column.id, e.target.checked)}
      style={{
        width: "16px",
        height: "16px",
        cursor: canUpdate ? "pointer" : "not-allowed",
        margin: "auto",
        display: "block",
      }}
    />
  );

  // Helper function to render text input cell
  const renderTextInputCell = (cell, row) => (
    <input
      type="text"
      disabled={!canUpdate}
      value={localValue[`${row.index}-${cell.column.id}`] ?? cell.getValue() ?? ""}
      onChange={(e) => handleTextInputChange(row.index, cell.column.id, e.target.value)}
      onBlur={(e) => {
        if (e.target.value !== row.original[cell.column.id]) {
          updateRow(row, cell.column.id, e.target.value);
        }
      }}
      style={{
        width: "100%",
        padding: "1px 2px",
        height: "24px",
        fontSize: "12px",
        border: "1px solid #ced4da",
      }}
    />
  );

  // Helper function to render dropdown cell
  const renderDropdownCell = (cell, row, columnDef) => (
    <Select
      isDisabled={!canUpdate}
      value={getDropdownValue(cell.getValue())}
      onChange={(selectedOption) =>
        updateRow(row, cell.column.id, selectedOption?.value, columnDef.fieldName)
      }
      options={getDropdownOptions(columnDef.fieldName, row.original)}
      isClearable={true}
      isSearchable
      placeholder="Select"
      menuPortalTarget={document.body}
      styles={getSelectStyles()}
    />
  );

  const renderCellContent = (cell, row, columnDef) => {
    // Check if this is the Transformation Rule column
    if (
      cell.column.id.toLowerCase().includes("transformation") ||
      columnDef.fieldName === "TransformationRule"
    ) {
      const handleStickyNoteClick = (e) => {
        e.stopPropagation();
        const rect = e.currentTarget.getBoundingClientRect();

        setStickyNotePopup({
          isOpen: true,
          value: cell.getValue() || "",
          rowIndex: row.index,
          columnId: cell.column.id,
          position: {
            top: rect.bottom + 8,
            left: rect.left,
          },
        });
      };

      const handleStickyNoteKeyDown = (e) => {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          handleStickyNoteClick(e);
        }
      };

      const handleStickyNoteFocus = (e) => {
        e.currentTarget.style.backgroundColor = "#f3f4f6";
      };

      const handleStickyNoteBlur = (e) => {
        e.currentTarget.style.backgroundColor = "transparent";
      };

      return (
        <button
          type="button"
          onClick={handleStickyNoteClick}
          onKeyDown={handleStickyNoteKeyDown}
          onFocus={handleStickyNoteFocus}
          onBlur={handleStickyNoteBlur}
          onMouseOver={handleStickyNoteFocus}
          onMouseOut={handleStickyNoteBlur}
          style={{
            cursor: "pointer",
            padding: "4px 8px",
            minHeight: "24px",
            display: "flex",
            alignItems: "center",
            color: cell.getValue() ? "#1f2937" : "#9ca3af",
            backgroundColor: "transparent",
            transition: "background-color 0.2s",
            borderRadius: "4px",
            border: "none",
            width: "100%",
            textAlign: "left",
          }}
          title="Click to edit in sticky notes"
          aria-label="Click to edit transformation rule in sticky notes"
        >
          <span
            style={{
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
              flex: 1,
            }}
          >
            {cell.getValue() || "Click to add transformation rule..."}
          </span>
        </button>
      );
    }

    if (columnDef.editType === "checkbox") {
      return renderCheckboxCell(cell, row);
    } else if (columnDef.editType === "text") {
      return renderTextInputCell(cell, row);
    } else if (columnDef.editType === "dropdown") {
      return renderDropdownCell(cell, row, columnDef);
    } else {
      return flexRender(cell.column.columnDef.cell, cell.getContext());
    }
  };

  // Helper function to render table cell
  const renderTableCell = (cell, row, columnDef) => {
    if (hideColumnsData.includes(columnDef?.columncategoryname?.toLowerCase())) {
      return null;
    }
    return (
      <td
        key={cell.id}
        style={{
          ...getFrozenColumnStyle(cell.column.id, false, false),
          padding: "2px 4px",
          border: "1px solid #dee2e6",
          width: columnSizes[cell.column.id] || cell.column.getSize(),
          maxWidth: columnSizes[cell.column.id] || cell.column.getSize(),
          fontSize: "12px",
          height: rowSizes[row.id] || "36px",
          whiteSpace: "nowrap",
          overflow: "hidden",
          textOverflow: "ellipsis",
        }}
      >
        {renderCellContent(cell, row, columnDef)}
      </td>
    );
  };

  // Helper function to render pinned table cell
  const renderPinnedTableCell = (cell, row, columnDef) => {
    if (hideColumnsData.includes(columnDef?.columncategoryname?.toLowerCase())) {
      return null;
    }
    return (
      <td
        key={cell.id}
        style={{
          ...getFrozenColumnStyle(cell.column.id, false, true),
          padding: "2px 4px",
          border: "1px solid #dee2e6",
          width: columnSizes[cell.column.id] || cell.column.getSize(),
          maxWidth: columnSizes[cell.column.id] || cell.column.getSize(),
          fontSize: "12px",
          height: rowSizes[row.id] || "36px",
          whiteSpace: "nowrap",
          overflow: "hidden",
          textOverflow: "ellipsis",
          position: "relative",
        }}
      >
        {renderCellContent(cell, row, columnDef)}
      </td>
    );
  };

  // Helper function to render row resize handle
  const renderRowResizeHandle = (rowId) => (
    <td
      style={{
        position: "absolute",
        bottom: 0,
        left: 0,
        right: 0,
        height: "5px",
        background: resizingRow === rowId ? "#2196f3" : "transparent",
        cursor: "row-resize",
        userSelect: "none",
        touchAction: "none",
        zIndex: 2,
      }}
      onMouseDown={(e) => handleRowResizeStart(e, rowId)}
    />
  );

  return (
    <tbody>
      {/* Pinned Rows */}
      {table.getTopRows().map((row) => {
        return (
          <tr
            key={row.id}
            style={{
              borderBottom: "1px solid #dee2e6",
              height: rowSizes[row.id] || "36px",
              backgroundColor: "#e3f2fd",
              position: "sticky",
              top: "36px",
              zIndex: 5,
            }}
          >
            {row.getVisibleCells().map((cell) => {
              const columnDef = cell.column.columnDef;
              return renderPinnedTableCell(cell, row, columnDef);
            })}
            {renderRowResizeHandle(row.id)}
          </tr>
        );
      })}

      {/* Regular Rows */}
      {table.getRowModel().rows.map((row) => {
        return (
          <tr
            key={row.id}
            style={{
              borderBottom: "1px solid #dee2e6",
              height: rowSizes[row.id] || "36px",
              backgroundColor: row.getIsSelected() ? "#f5f5f5" : "white",
              position: "relative",
            }}
          >
            {row.getVisibleCells().map((cell) => {
              const columnDef = cell.column.columnDef;
              return renderTableCell(cell, row, columnDef);
            })}
            {renderRowResizeHandle(row.id)}
          </tr>
        );
      })}
    </tbody>
  );
};

TableBody.propTypes = {
  table: PropTypes.object.isRequired,
  hideColumnsData: PropTypes.array.isRequired,
  columnSizes: PropTypes.object.isRequired,
  rowSizes: PropTypes.object.isRequired,
  resizingColumn: PropTypes.string,
  resizingRow: PropTypes.string,
  frozenColumns: PropTypes.array,
  canUpdate: PropTypes.bool,
  localValue: PropTypes.object,
  dependentValues: PropTypes.object,
  sourceTableColumnName: PropTypes.string,
  dropdownOptions: PropTypes.array,
  handleColumnResizeStart: PropTypes.func,
  handleRowResizeStart: PropTypes.func,
  toggleColumnFreeze: PropTypes.func,
  updateCheckboxValue: PropTypes.func,
  handleTextInputChange: PropTypes.func,
  updateRow: PropTypes.func,
  setStickyNotePopup: PropTypes.func,
  getFrozenColumnStyle: PropTypes.func,
  getFrozenColumnLeftPosition: PropTypes.func,
};

export default TableBody;